import { RefObject, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { VerificationChoice } from '~/components/VerificationModal/types';
import VerificationModal from '~/components/VerificationModal/VerificationModal';
import { errorMessages } from '~/constants/errorMessages.ts';
import useGetUserRole from '~/hooks/useGetUserRole.ts';
import { useAppDispatch } from '~/store/appDispatch';
import { clearDiiaState, setDiiaSignError } from '~/store/diia/slices';
import {
  deleteDiiaVerification,
  diiaVerificationGetFinalStatus,
  diiaVerificationGetFinalStatusAssetManager,
  diiaVerificationGetStatus,
  diiaVerificationGetStatusAssetManager,
  diiaVerificationInitialize,
  diiaVerificationInitializeAssetManager,
  diiaVerificationRetrieveDocuments,
  diiaVerificationRetrieveDocumentsAssetManager,
  updateFromDiiaVerification,
} from '~/store/diia/thunks/identificationVerificationThunks';
import { DiiaStatuses } from '~/store/diia/types';
import {
  createApplication,
  fetchCreatedIdentificationFormAssetManager,
  videoVerificationAssetManagers,
} from '~/store/identification-form/thunks/thunks';
import { ERROR_CODES, HTTP_ERROR_STATUS } from '~/constants/errorCodes.ts';
import { errorCodes } from '../constants.tsx';
import { useFormContext, useWatch } from 'react-hook-form';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import * as Sentry from '@sentry/react';
import { setIdentificationAppId } from '~/store/identification-form/slices.ts';
import { TErrorPayload } from '../types.ts';
import { selectTokenData } from '~/store/users/selectors.ts';
import { selectDiia } from '~/store/diia/selectors.ts';
import { selectApplicationId } from '~/store/identification-form/selectors.ts';
import { TimerHandle } from '~/components/Timer/Timer.tsx';
import { TThunkError } from '~/store/covenants/types.ts';

interface IVerificationBlockProps {
  isVerificationModalOpened: boolean;
  handleCloseModal: () => void;
  setError: (text: string) => void;
  onNextStep: () => void;
  handleVerificationSuccess: (
    verificationType: VerificationChoice,
    applicationId: number,
    applicationNumber: string
  ) => Promise<void>;
  isVideoVerification: boolean;
  changeVideoVerification: boolean;
  clientType?: string;
}

const VerificationBlock = ({
  handleCloseModal,
  setError,
  onNextStep,
  isVerificationModalOpened,
  handleVerificationSuccess,
  isVideoVerification,
  changeVideoVerification = false,
  clientType,
}: IVerificationBlockProps) => {
  const dispatch = useAppDispatch();
  const { isAssetManager } = useGetUserRole();
  const { setValue, control } = useFormContext();
  const tokenData = useSelector(selectTokenData);
  const { loading: isLoading, error: diiaError, deepLink, status } = useSelector(selectDiia) || {};
  const appId = useSelector(selectApplicationId) || null;
  const timerRef = useRef<{ resetTimer: () => void; startTimer: () => void }>(null);
  const [identificationNumber] = useWatch({
    control: control,
    name: ['identificationNumber'],
  });
  const [requestId, setRequestId] = useState<string>('');
  const [applicationId, setApplicationId] = useState<number | null>(null);
  const [getStatusPromise, setGetStatusPromise] = useState<{ abort: () => void } | null>(null);

  const resetDiiaAttemptState = () => {
    setApplicationId(null);
    setRequestId('');
    setGetStatusPromise(null);
  };

  const invalidateClientApplication = () => {
    if (isAssetManager || changeVideoVerification) return;

    dispatch(setIdentificationAppId(undefined));
    setValue('applicationId', undefined, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    resetDiiaAttemptState();
  };

  const closeModal = () => {
    if (isAssetManager) return;
    handleCloseModal();
    getStatusPromise?.abort();
    dispatch(clearDiiaState());
    resetDiiaAttemptState();
  };

  useEffect(() => {
    (async () => {
      try {
        if (status === DiiaStatuses.rejected && requestId) {
          if (isAssetManager) {
            await dispatch(
              diiaVerificationGetFinalStatusAssetManager({
                applicationId: tokenData.appId ?? '',
                managerId: tokenData.managerId,
                requestId,
              })
            ).unwrap();
          } else {
            await dispatch(
              diiaVerificationGetFinalStatus({
                requestId,
                applicationId: applicationId ?? '',
              })
            ).unwrap();
          }
        }
      } catch {
        handleVerificationError(diiaError || { status: DiiaStatuses.canceled });
      }
    })();
  }, [status, requestId]);

  const handleDiiaVerificationAssetManager = async () => {
    if (isLoading) return;
    try {
      const { requestId } = await dispatch(
        diiaVerificationInitializeAssetManager({
          managerId: tokenData.managerId ?? '',
          applicationId: tokenData.appId ?? '',
        })
      ).unwrap();
      setRequestId(requestId);
      timerRef.current?.startTimer();
      const body = {
        requestId,
        managerId: tokenData.managerId,
        applicationId: tokenData.appId ?? '',
      };
      const promise = dispatch(diiaVerificationGetStatusAssetManager(body));
      setGetStatusPromise(promise);
      const payload = await promise.unwrap();
      setGetStatusPromise(null);

      if (payload?.status === DiiaStatuses.notUsed) {
        dispatch(setDiiaSignError(errorMessages.DOCUMENT_SIGNING_FAILED));
      }
      if (isLoading) return;

      if (payload?.status === DiiaStatuses.processing) {
        const finalStatus = await dispatch(
          diiaVerificationGetFinalStatusAssetManager(body)
        ).unwrap();

        if (finalStatus.status === DiiaStatuses.done) {
          closeModal();
          await dispatch(diiaVerificationRetrieveDocumentsAssetManager(body)).unwrap();
          handleVerificationSuccessAssetManager(VerificationChoice.DIIA);
        }
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleDiiaVerification = async () => {
    if (isLoading) return;

    let applicationId = appId;
    let applicationNumber;
    try {
      if (!changeVideoVerification && !applicationId) {
        const response = await dispatch(
          createApplication({
            identifyCode: identificationNumber,
          })
        ).unwrap();
        applicationId = response.id;
        applicationNumber = response.appNumber;
      } else {
        applicationId = appId;
      }
      setApplicationId(applicationId);

      const { requestId } = await dispatch(
        diiaVerificationInitialize({
          applicationId: applicationId ?? '',
        })
      ).unwrap();

      setRequestId(requestId);
      timerRef.current?.startTimer();

      const promise = dispatch(
        diiaVerificationGetStatus({
          requestId,
          applicationId: applicationId as number,
        })
      );
      setGetStatusPromise(promise);
      let response;
      try {
        response = await promise.unwrap();
      } catch (error) {
        const responseError = error as TErrorPayload;
        if (responseError?.data?.errorCode || responseError?.data?.status) {
          if (responseError?.data?.errorCode === ERROR_CODES.DOCUMENT_FETCH_FAILED) {
            dispatch(setDiiaSignError(responseError?.data?.message as string));
          }
          invalidateClientApplication();
        }
        return;
      } finally {
        setGetStatusPromise(null);
      }

      if (response?.status === DiiaStatuses.notUsed) {
        invalidateClientApplication();
        dispatch(setDiiaSignError(errorMessages.DOCUMENT_SIGNING_FAILED));
        return;
      }

      if (response?.status === DiiaStatuses.processing) {
        const finalStatus = await dispatch(
          diiaVerificationGetFinalStatus({
            requestId,
            applicationId: applicationId ?? '',
          })
        ).unwrap();

        if (changeVideoVerification) {
          await dispatch(
            deleteDiiaVerification({
              applicationId: applicationId ?? '',
            })
          ).unwrap();
        }

        if (finalStatus.status === DiiaStatuses.done) {
          closeModal();
          await dispatch(
            diiaVerificationRetrieveDocuments({
              requestId,
              applicationId: applicationId ?? '',
            })
          ).unwrap();
          if (changeVideoVerification) {
            await dispatch(
              updateFromDiiaVerification({
                applicationId: applicationId ?? '',
              })
            ).unwrap();
          }
          await handleVerificationSuccess(
            VerificationChoice.DIIA,
            applicationId as number,
            applicationNumber as string
          );
        }
      } else {
        invalidateClientApplication();
        handleVerificationError(diiaError);
      }
    } catch (error) {
      const responseError = error as TErrorPayload;
      if (responseError?.data?.errorCode === ERROR_CODES.DOCUMENT_FETCH_FAILED) {
        dispatch(setDiiaSignError(responseError?.data?.message as string));
      }
      invalidateClientApplication();
      Sentry.captureException(error);
    }
  };

  const handleVerificationSuccessAssetManager = async (type: VerificationChoice) => {
    setValue('verification', type);
    try {
      await dispatch(
        type === VerificationChoice.DIIA
          ? fetchCreatedIdentificationFormAssetManager({
              managerId: tokenData.managerId,
              applicationId: tokenData.appId,
            })
          : videoVerificationAssetManagers({
              managerId: tokenData.managerId,
              applicationId: tokenData.appId,
            })
      ).unwrap();

      onNextStep();
    } catch (error) {
      console.log(error);
      handleVerificationError(error);
    }
  };

  const handleVideoVerification = async () => {
    try {
      if (isAssetManager) {
        handleVerificationSuccessAssetManager(VerificationChoice.VIDEO);
      } else {
        const response = await dispatch(
          createApplication({ identifyCode: identificationNumber })
        ).unwrap();
        handleVerificationSuccess(VerificationChoice.VIDEO, response.id, response.appNumber);
      }
    } catch (error) {
      handleVerificationError(error);
    }
  };

  const handleVerificationError = (error: unknown) => {
    const thunkError = error as TThunkError & { data: TThunkError };
    if (thunkError.status) {
      if (thunkError.status === HTTP_ERROR_STATUS.INTERNAL_SERVER_ERROR) {
        if (thunkError.data?.errorCode === ERROR_CODES.YOU_SCORE_API_ERROR.toString()) {
          setError(errorMessages.SERVICE_UNAVAILABLE);
        } else if (errorCodes.includes(Number(thunkError.data.errorCode))) {
          setError(thunkError.data.message);
        } else {
          setError(errorMessages.SERVICE_UNAVAILABLE);
        }
      }
    } else if (thunkError.message) {
      setError(thunkError.message);
    } else {
      setError(errorMessages.SERVICE_UNAVAILABLE);
    }
  };

  const handleCloseError = () => {
    dispatch(setDiiaSignError(''));
    closeModal();
  };

  return (
    <>
      <VerificationModal
        isModalOpen={isVerificationModalOpened}
        onClose={closeModal}
        handleDiiaVerification={
          isAssetManager ? handleDiiaVerificationAssetManager : handleDiiaVerification
        }
        handleVideoVerification={handleVideoVerification}
        deepLink={deepLink}
        timerRef={timerRef as RefObject<TimerHandle>}
        isVideoVerification={isVideoVerification}
        clientType={clientType}
      />
      {!!diiaError && (
        <ModalAlert isOpen={!!diiaError} text={diiaError} onClose={handleCloseError} />
      )}
    </>
  );
};

export default VerificationBlock;
